import { useQuery } from '@tanstack/react-query';

import type {
  ActionLogResponse, ModelLogResponse, RequestLogResponse, ResponseLogResponse
} from '@/api';
import APIGateway from '@/api';

export type TraceRequestFilters = {
  requestId?: string;
  correlationId?: string;
  sessionId?: string;
  startTime?: string;
  endTime?: string;
};

export const traceKeys = {
  all: [ 'traces' ] as const,
  requests: ( agentName: string, batchSize: number, offset: number, filters?: TraceRequestFilters ) => [
    ...traceKeys.all, 'requests', agentName, batchSize, offset, filters
  ] as const,
  traceModels: ( agentName: string, requestId: string ) => [
    ...traceKeys.all, 'models', agentName, requestId
  ] as const,
  traceActions: ( agentName: string, requestId: string ) => [
    ...traceKeys.all, 'actions', agentName, requestId
  ] as const,
  traceResponses: ( callId: string ) => [
    ...traceKeys.all, 'responses', callId
  ] as const
};

/** Fetch the list of completion requests for the request list panel */
export function useTraceRequestsQuery(
  agentName: string | undefined,
  batchSize: number = 20,
  offset: number = 0,
  filters?: TraceRequestFilters
) {
  return useQuery( {
    queryKey: traceKeys.requests( agentName!, batchSize, offset, filters ),
    queryFn: async(): Promise<RequestLogResponse> => {
      if ( !agentName ) throw new Error( 'Agent name required' );
      return APIGateway.logs.requestLogsGet(
        agentName, batchSize, offset,
        filters?.requestId,
        filters?.correlationId,
        filters?.sessionId,
        filters?.startTime,
        filters?.endTime
      );
    },
    enabled: !!agentName,
    staleTime: 30000,
    retry: 1
  } );
}

/** Fetch model logs (private, with messages) for a specific request */
export function useTraceModelLogsQuery(
  agentName: string | undefined,
  requestId: string | null
) {
  return useQuery( {
    queryKey: traceKeys.traceModels( agentName!, requestId! ),
    queryFn: async(): Promise<ModelLogResponse> => {
      if ( !agentName || !requestId ) throw new Error( 'Agent name and request ID required' );
      return APIGateway.logs.modelLogsGet( agentName, 100, 0, requestId );
    },
    enabled: !!agentName && !!requestId,
    staleTime: 60000,
    retry: 1
  } );
}

/** Fetch action logs for a specific request */
export function useTraceActionLogsQuery(
  agentName: string | undefined,
  requestId: string | null
) {
  return useQuery( {
    queryKey: traceKeys.traceActions( agentName!, requestId! ),
    queryFn: async(): Promise<ActionLogResponse> => {
      if ( !agentName || !requestId ) throw new Error( 'Agent name and request ID required' );
      return APIGateway.logs.actionLogsGet( agentName, 100, 0, requestId );
    },
    enabled: !!agentName && !!requestId,
    staleTime: 60000,
    retry: 1
  } );
}

/** Fetch completion responses for a specific call_id */
export function useTraceResponsesQuery( callId: string | null ) {
  return useQuery( {
    queryKey: traceKeys.traceResponses( callId! ),
    queryFn: async(): Promise<ResponseLogResponse> => {
      if ( !callId ) throw new Error( 'Call ID required' );
      return APIGateway.logs.responseLogsGet( callId );
    },
    enabled: !!callId,
    staleTime: 60000,
    retry: 1
  } );
}
